import { useAppSettingsDictionary } from '@/shared/api';
import { useMemo, type ElementType } from 'react';
import type { AccountOption } from './types';
import type { ApiTokenAccessKey } from '@/shared/types';
import WalletIcon from '@/shared/assets/wallet-icon.svg?react';
import InvestAccountIcon from '@/shared/assets/invest-account-icon.svg?react';
import SpotAccountIcon from '@/shared/assets/spot-account-icon.svg?react';
import PercentIcon from '@/shared/assets/percent-icon.svg?react';
import EarnAccountIcon from '@/shared/assets/earn-account-icon.svg?react';
import {
  ACCOUNT_TYPES,
  useAccountsRests,
  type AccountsMarginRestsBaseResponseDto,
  type AccountsRestsBaseResponseDto,
  type AccountType,
} from '@/entities/account';
import { useTranslation } from 'react-i18next';
import { applyAmountPrecision, sum } from '@/shared/lib';

const ICON_BY_SECTION: Record<AccountType, ElementType> = {
  ewallet: WalletIcon,
  invest: InvestAccountIcon,
  spot: SpotAccountIcon,
  leverage: PercentIcon,
  earn: EarnAccountIcon,
};

const TITLE_BY_SECTION: Record<AccountType, string> = {
  ewallet: 'personalAccount.accountsSection.ewalletTitle',
  invest: 'personalAccount.accountsSection.investTitle',
  spot: 'personalAccount.accountsSection.spotTitle',
  leverage: 'personalAccount.accountsSection.leverageTitle',
  earn: 'personalAccount.accountsSection.earnTitle',
};

type BlockType =
  | AccountsRestsBaseResponseDto
  | AccountsMarginRestsBaseResponseDto;

export const useApiTokensModalOptions = () => {
  const { t } = useTranslation();

  const { appSettingsData, isAppSettingsLoading, appSettingsError } =
    useAppSettingsDictionary();

  const {
    data: accountsRests,
    isLoading: isAccountsRestsLoading,
    error: accountsRestsError,
  } = useAccountsRests();

  const accessTypesMap = useMemo<Record<ApiTokenAccessKey, string>>(() => {
    const list = appSettingsData?.value.tradingWebapiTokenAccessList;
    return list ? Object.assign({}, ...list) : {};
  }, [appSettingsData]);

  const accounts = useMemo<AccountOption[]>(() => {
    if (!accountsRests) return [];

    const result: AccountOption[] = [];

    Object.entries(accountsRests).forEach(([section, value]) => {
      if (!value) return;

      const icon = ICON_BY_SECTION[section as AccountType];
      const title = TITLE_BY_SECTION[section as AccountType];

      const blocks: BlockType[] = Array.isArray(value) ? value : [value];

      blocks.forEach(block => {
        block.accounts?.forEach(acc => {
          if (acc.accTypeName !== ACCOUNT_TYPES.Trading) return;

          const amount =
            'assets' in acc && acc.assets?.length
              ? sum(acc.assets.map(asset => asset.baseAvailable ?? 0))
              : 'equity' in acc
                ? (acc.equity ?? 0)
                : 0;

          const precision =
            'balanceAsset' in acc && acc.balanceAsset?.precision
              ? acc.balanceAsset.precision
              : block.basePrecission;

          const assetCode =
            'balanceAsset' in acc && acc.balanceAsset?.code
              ? acc.balanceAsset.code
              : block.baseCode;

          result.push({
            icon,
            title: t(title),
            name: acc.accName || acc.accNum,
            balance: applyAmountPrecision(amount, precision, 'credit'),
            accountNumber: acc.accNum,
            accountType: section as AccountType,
            baseCode: assetCode,
            basePrecision: precision,
            tradingAccKey: acc.tradingAccKey,
          });
        });
      });
    });

    return result;
  }, [accountsRests]);

  return {
    accessTypesMap,
    accounts,
    isLoading: isAppSettingsLoading || isAccountsRestsLoading,
    error: appSettingsError || accountsRestsError,
  };
};
